import React, { useEffect, useState } from "react";
import axios from "axios";
import AlvnaBrand from "../components/AlvnaBrand";
import { endpoints } from "../apiConfig";
import { colors, fonts, space, radius, buttonPrimary } from "../theme";

export default function FeeAgreementPage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";

  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => axios.get(`${endpoints.feeAgreement}?token=${token}`).then((res) => setInfo(res.data));

  useEffect(() => {
    if (!token) {
      setError("This link is missing its token.");
      return;
    }
    load().catch((err) => setError(err.response?.data?.error || "This link is invalid or has expired."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const agree = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(endpoints.feeAgreement, { token });
      // Re-read the invoice so the page always reflects what the server
      // recorded (agreed, or already paid / replaced in the meantime).
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const isInvoice = info?.type === "invoice";
  const amount = info ? `$${info.fee_amount.toFixed(2)}` : "";
  const paidOn = info?.paid_at ? new Date(`${info.paid_at.replace(" ", "T")}Z`).toLocaleDateString() : "";

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <AlvnaBrand variant="full" businessName={info?.business_name} />
        <div style={{ padding: space[5], textAlign: "center" }}>
          {error ? (
            <p style={styles.error}>{error}</p>
          ) : !info ? (
            <p style={{ fontFamily: fonts.body, color: colors.textSecondary }}>Loading…</p>
          ) : info.state === "replaced" ? (
            <>
              <h2 style={styles.heading}>This invoice is no longer active</h2>
              <p style={{ fontFamily: fonts.body, color: colors.textSecondary }}>
                It was replaced by a newer invoice or settled by another payment. Please use the
                most recent email from Alvna, or contact contact@alvna.com if you have questions.
              </p>
            </>
          ) : info.state === "paid" ? (
            <>
              <h2 style={styles.heading}>Paid, thank you!</h2>
              <p style={{ fontFamily: fonts.body, color: colors.textSecondary }}>
                We've received your payment of {amount}{paidOn ? ` on ${paidOn}` : ""}. Nothing more is needed.
              </p>
            </>
          ) : info.state === "agreed" ? (
            <>
              <h2 style={styles.heading}>Thank you!</h2>
              <p style={{ fontFamily: fonts.body, color: colors.textSecondary }}>
                Your agreement to the {amount} {isInvoice ? "subscription invoice" : "fee"} has been recorded.
                Alvna will follow up separately to arrange payment.
              </p>
            </>
          ) : (
            <>
              <h2 style={styles.heading}>{isInvoice ? "Confirm Subscription Invoice" : "Confirm Limit Increase Fee"}</h2>
              <p style={{ fontFamily: fonts.body, color: colors.textSecondary, marginBottom: space[4] }}>
                {isInvoice
                  ? "Your Alvna Remo subscription invoice is:"
                  : "To proceed with your requested limit increase, there's a fee of:"}
              </p>
              <div style={styles.amount}>{amount}</div>
              <p style={{ fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, marginBottom: space[4] }}>
                Clicking below confirms you agree to {isInvoice ? "this invoice" : "this fee"}. Alvna will contact you
                separately to arrange payment. No payment is collected on this page.
              </p>
              <button style={buttonPrimary} onClick={agree} disabled={submitting}>
                {submitting ? "Confirming…" : `I Agree to Pay ${amount}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: colors.bgPage, padding: space[4], boxSizing: "border-box" },
  card: { width: "100%", maxWidth: 400, background: colors.surface, borderRadius: radius.lg, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" },
  heading: { margin: "0 0 8px", fontFamily: fonts.display, fontSize: 19, fontWeight: 600, color: colors.textPrimary },
  amount: { fontFamily: fonts.display, fontSize: 36, fontWeight: 700, color: colors.textPrimary, margin: `${space[3]} 0` },
  error: { color: "#b91c1c", fontSize: 14, fontFamily: fonts.body },
};
